import {
  ArrowRight2,
  ArrowDown2,
  ArrowUp2,
  Check,
  CloseCircle,
  CloseSquare,
  DocumentUpload,
  Edit,
  Edit2,
  Trash,
  Warning2,
} from "iconsax-react";
import { useEffect, useState } from "react";
import AuthButton from "../../buttons/AuthButton";
import NormalInputField, {
  NormalTextAreaField,
} from "../../inputs/NormalInputField";
import NormalSelectInputField from "../../inputs/NormalSelectInputField";
import PrimaryAddButton from "../../buttons/PrimaryAddButton";
import instance from "../../../axios/instance";
import { getToken } from "../../../utils/token";
import { Autocomplete, TextField } from "@mui/material";
import { generateRandomId } from "../../../utils/data";
import AddPatientModal from "../AddPatientModal";
import { OrderSummaryModal } from "./OrderSummary";
import countries from "../../../../countries.json";
import { toast } from "react-toastify";
import CompoundDrugAutocomplete from "./CompoundDrugAutocomplete";
import { reasonsForCompounding } from "../../../../../distributors/src/utils/data";
import UploadPatientsCsvModal from "./UploadPatientsCsvModal";
import directionsSigs from "../../../utils/directions_sig";

const createEmptyMedication = () => ({
  id: generateRandomId(),
});

const getDirectionCountFromDaysSupply = (daysOfSupply) => {
  const normalizedDays = Number(daysOfSupply);

  if (!Number.isFinite(normalizedDays) || normalizedDays <= 0) {
    return 1;
  }

  return Math.max(1, Math.ceil(normalizedDays / 30));
};

const getMonthDirectionLabel = (monthNumber) => {
  const suffixMap = {
    1: "st",
    2: "nd",
    3: "rd",
  };
  const suffix =
    monthNumber % 100 >= 11 && monthNumber % 100 <= 13
      ? "th"
      : (suffixMap[monthNumber % 10] ?? "th");

  return `${monthNumber}${suffix} month direction`;
};

const getDirectionText = (direction) => {
  if (typeof direction === "string") return direction;

  return direction?.text || direction?.direction || direction?.sig || "";
};

const buildDirectionEntries = (count, existingDirections = []) =>
  Array.from({ length: count }, (_, index) => ({
    month: index + 1,
    label: getMonthDirectionLabel(index + 1),
    text: getDirectionText(existingDirections[index]),
  }));

const filterShippingServicesForDeliveryType = (services = [], deliveryType) =>
  deliveryType === "Deliver to Doctor"
    ? services.filter((service) => {
      const serviceName = `${service?.name || ""} ${service?.service_level || ""} ${service?.label || ""}`
        .toLowerCase()
        .trim();

      return !(
        serviceName.includes("pickup") ||
        serviceName.includes("pick up")
      );
    })
    : services;

const normalizeMedicationDirections = (medication = {}, patientOrder) => {
  const directionCount = getDirectionCountFromDaysSupply(
    medication.daysOfSupply || medication.dayOfSupply,
  );
  const existingDirections = Array.isArray(medication.directions)
    ? medication.directions
    : medication.direction
      ? String(medication.direction)
        .split("|")
        .map((direction, index) => ({
          month: index + 1,
          label: getMonthDirectionLabel(index + 1),
          text: direction.trim(),
        }))
      : [];

  const directions = patientOrder
    ? buildDirectionEntries(directionCount, existingDirections)
    : [];
  const primaryDirection =
    directions
      .map((entry) => entry.text?.trim())
      .filter(Boolean)
      .join(" | ") ||
    medication.direction ||
    "";

  return {
    ...medication,
    directions,
    direction: primaryDirection,
  };
};

export default function CreateOrder(props) {
  const token = getToken();
  const selectedRx = Array.isArray(props.selectedRx) ? props.selectedRx : [];

  const [numberOfRows, setNumberOfRows] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [orderType, setOrderType] = useState(
    selectedRx.length >= 1 ? "Refill" : "New",
  );
  const [orderBillingPreference, setOrderBillingPreference] = useState("");
  const [deliveryType, setDeliveryType] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [city, setCity] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [deliveryPhone, setDeliveryPhone] = useState("");
  const [pharmacy, setPharmacy] = useState("");
  const [patient, setPatient] = useState(props.patientId || "");
  const [medications, setMedications] = useState([]);
  const [shippingServices, setShippingServices] = useState([]);
  const [shippingService, setShippingService] = useState("");
  const [pharmacies, setPharmacies] = useState([]);
  const [pharmacyDrugs, setPharmacyDrugs] = useState([]);
  const [orderMethod, setOrderMethod] = useState(
    props.patientId ? "Patient" : "",
  );
  const [orderNote, setOrderNote] = useState("");
  const [facility, setFacility] = useState(null);
  const [patients, setPatients] = useState([]);
  const [file, setFile] = useState(null);
  const [isAddPatientModalOpen, setIsAddPatientModalOpen] = useState(false);
  const [isUploadCsvModalOpen, setIsUploadCsvModalOpen] = useState(false);
  const [isAddDoctorModalOpen, setIsAddDoctorModalOpen] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [supervisingPractitionerId, setSupervisingPractitionerId] =
    useState("");
  const [showSummary, setShowSummary] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("");
  const [formulations, setFormulations] = useState([]);
  const [editingMedication, setEditingMedication] = useState(null);
  const [multiPatientOrders, setMultiPatientOrders] = useState([]);
  const [openAccordionIndex, setOpenAccordionIndex] = useState(0);

  const onFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const hasMedication =
    medications.length >= 1 ? medications.filter((med) => med.name) : [];
  const hasMultiPatientMedication =
    multiPatientOrders.length >= 1 &&
    multiPatientOrders.every(
      (order) =>
        Array.isArray(order.medications) &&
        order.medications.filter((med) => med?.name).length >= 1,
    );

  const isMultiPatientDoctorDelivery =
    orderMethod === "multi-patient" && deliveryType === "Deliver to Doctor";

  const isOrderNoteRequired = props.medType === "compound";

  const isMultiPatientValid =
    orderMethod === "multi-patient" &&
    multiPatientOrders.length > 0 &&
    (isMultiPatientDoctorDelivery
      ? deliveryAddress &&
      deliveryPhone &&
      shippingService &&
      state &&
      city &&
      zipCode
      : multiPatientOrders.every(
        (o) =>
          o.deliveryAddress &&
          o.deliveryPhone &&
          o.city &&
          o.state &&
          o.zipCode &&
          o.shippingService,
      ));

  const isSinglePatientValid =
    orderMethod !== "multi-patient" &&
    deliveryAddress &&
    deliveryPhone &&
    shippingService &&
    state &&
    city &&
    zipCode;

  const isButtonNotActive =
    !orderType ||
    !deliveryType ||
    !pharmacy ||
    (orderMethod === "multi-patient"
      ? !hasMultiPatientMedication
      : hasMedication.length < 1) ||
    !orderMethod ||
    !orderBillingPreference ||
    !supervisingPractitionerId ||
    (isOrderNoteRequired && !orderNote) ||
    (!isMultiPatientValid && !isSinglePatientValid);

  // update medication state
  const updateMedication = (prevMedications, index, field, value) => {
    const updatedMedications = [...prevMedications];
    const medication = updatedMedications[index] || {};
    medication[field] = value;
    updatedMedications[index] = medication;
    return updatedMedications;
  };

  const fetchDoctors = async () => {
    const doctorsRes = await instance.get("/doctor/facility/practitioners", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (doctorsRes.data.length >= 1) {
      setDoctors(
        doctorsRes.data
          .map((doctor) => ({
            label: `${doctor.lastname} ${doctor.firstname}`,
            value: doctor.id,
          })),
      );
    } else {
      setDoctors([]);
    }

    return doctorsRes.data;
  };

  useEffect(() => {
    let loaded = true;
    (async () => {
      try {
        const pharmacies = await instance.get("/doctor/pharmacy/all", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const facility = await instance.get("/doctor/facility", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const doctorsRes = await instance.get("/doctor/facility/practitioners", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (loaded) {
          setFacility(facility.data);
          if (pharmacies.data.length >= 1) {
            setPharmacies(
              pharmacies.data.map((pharm) => ({
                label: pharm.pharmacy_name,
                value: pharm.id,
              })),
            );
          } else {
            setPharmacies([]);
          }
          if (doctorsRes.data.length >= 1) {
            setDoctors(
              doctorsRes.data
                .map((doctor) => ({
                  label: `${doctor.lastname} ${doctor.firstname}`,
                  value: doctor.id,
                })),
            );
          } else {
            setDoctors([]);
          }
        }
      } catch (e) {
        setErrorMessage("Failed to fetch pharmacies, please reload page");
        console.log(e);
      }
    })();

    return () => {
      loaded = false;
    };
  }, []);

  useEffect(() => {
    let loaded = true;
    if (showSummary && orderId) {
      (async () => {
        try {
          const order = await instance.post(
            "/doctor/order/single",
            { orderId },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
          if (loaded) {
            setOrder(order.data);
          }
        } catch (e) {
          props.triggerRevalidate();
          props.onClose();
        }
      })();
    }

    return () => {
      loaded = false;
    };
  }, [showSummary, orderId]);

  const validateMedication = (medications) => {
    const requiredFields =
      props.medType === "compound"
        ? ["name", "quantity", "size", "direction", "ingredients"]
        : ["name", "quantity", "size", "mg", "direction"];
    let response = { valid: true };
    medications.forEach((med) => {
      if (orderType?.toLowerCase() === "refill" && !med.rxId) {
        response = { valid: false, message: "Rx ID is required for a refill" };
        return;
      }

      const requiredFieldVals = requiredFields.filter((field) => !med[field]);
      if (requiredFieldVals.length >= 1) {
        response = {
          valid: false,
          message: `Required medication fields (${requiredFieldVals.join(
            ", ",
          )}) are missing`,
        };
      }

      if (orderMethod.toLowerCase() !== "bulk") {
        const directionCount = getDirectionCountFromDaysSupply(
          med.daysOfSupply || med.dayOfSupply,
        );
        const directions = Array.isArray(med.directions) ? med.directions : [];
        const invalidDirections =
          directions.length !== directionCount ||
          directions.some((direction) => !direction?.text?.trim());

        if (invalidDirections) {
          response = {
            valid: false,
            message: `Provide ${directionCount} direction${directionCount > 1 ? "s" : ""
              } for ${med.name || "the selected medication"}`,
          };
        }
      }
    });

    if (props.medType === "compound") {
      if (
        orderMethod.toLowerCase() !== "bulk" &&
        !medications[0].reasonForCompounding
      ) {
        response = {
          valid: false,
          message: "A medical justification is required for this order",
        };
      }

      if (medications.some((med) => med.ingredients.length === 0)) {
        response = {
          valid: false,
          message:
            "At least one ingredient is required for compound medications",
        };
      }

      // check for each ingredients to make sure they are valid
      medications.forEach((med) => {
        med.ingredients.forEach((ingredient) => {
          if (!ingredient.name || !ingredient.quantity) {
            response = {
              valid: false,
              message: "All ingredients must have a name, and quantity",
            };
          }
        });
      });
    }

    return response;
  };

  const validateMultiPatientMedication = () => {
    for (const patientOrder of multiPatientOrders) {
      const patientMedications = Array.isArray(patientOrder.medications)
        ? patientOrder.medications
        : [];

      if (patientMedications.filter((med) => med?.name).length < 1) {
        return {
          valid: false,
          message: `Select medication for ${patientOrder.patientName}`,
        };
      }

      const medicationValidation = validateMedication(patientMedications);
      if (!medicationValidation.valid) {
        return {
          valid: false,
          message: `${patientOrder.patientName}: ${medicationValidation.message}`,
        };
      }
    }

    return { valid: true };
  };

  const handleCreateOrder = (e) => {
    e.preventDefault();
    setErrorMessage("");
    const medRes =
      orderMethod === "multi-patient"
        ? validateMultiPatientMedication()
        : validateMedication(medications);
    if (!medRes.valid) {
      return setErrorMessage(medRes.message);
    }

    const amount =
      orderMethod === "multi-patient"
        ? multiPatientOrders.reduce((total, patientOrder) => {
          const patientAmount = (patientOrder.medications || []).reduce(
            (medicationTotal, drug) => medicationTotal + (+drug.amount || 0),
            0,
          );
          return total + patientAmount;
        }, 0)
        : medications.reduce((total, drug) => {
          return total + +drug.amount;
        }, 0);

    setIsLoading(true);
    const patients =
      orderMethod === "multi-patient"
        ? multiPatientOrders.map((patientOrder) => ({
          patientId: patientOrder.patientId,
          patientName: patientOrder.patientName,
          deliveryAddress: isMultiPatientDoctorDelivery
            ? deliveryAddress
            : patientOrder.deliveryAddress,
          state: isMultiPatientDoctorDelivery ? state : patientOrder.state,
          city: isMultiPatientDoctorDelivery ? city : patientOrder.city,
          zipCode: isMultiPatientDoctorDelivery
            ? zipCode
            : patientOrder.zipCode,
          deliveryPhone: isMultiPatientDoctorDelivery
            ? deliveryPhone
            : patientOrder.deliveryPhone,
          shippingService: isMultiPatientDoctorDelivery
            ? shippingService
            : patientOrder.shippingService,
          medication: patientOrder.medications,
        }))
        : [
          {
            patientId: patient,
            patientName: patient.name,
            deliveryAddress: deliveryAddress,
            state,
            city,
            zipCode,
            deliveryPhone,
            shippingService,
          },
        ];
    const formData = new FormData();
    formData.append("file", file);
    formData.append("patientId", patient);
    formData.append("orderType", orderType);
    formData.append("orderBillingPreference", orderBillingPreference);
    formData.append("deliveryType", deliveryType);
    formData.append("orderMethod", orderMethod);
    formData.append("pharmacyId", pharmacy);
    formData.append(
      "medication",
      JSON.stringify(
        orderMethod.toLowerCase() === "multi-patient" ? [] : medications,
      ),
    );
    formData.append("orderNote", orderNote);
    formData.append("supervisingPractitionerId", supervisingPractitionerId);
    formData.append("medType", props.medType);
    formData.append("amount", amount || "");
    formData.append("case_ids", "");
    let endpoint = "/doctor/order/create";
    if (["multi-patient", "patient"].includes(orderMethod.toLowerCase())) {
      formData.append("patients", JSON.stringify(patients));
      endpoint = "/doctor/order/create";
    } else {
      formData.append("shippingService", JSON.stringify(shippingService));
      formData.append("deliveryAddress", deliveryAddress);
      formData.append("deliveryState", state);
      formData.append("deliveryCity", city);
      formData.append("deliveryZipCode", zipCode);
      formData.append("deliveryPhone", deliveryPhone);
      endpoint = "/doctor/order/create/bulk";
    }
    instance
      .post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        // props.triggerRevalidate();
        // props.onClose();
        setOrderId(response.data.orderId);
        setShowSummary(true);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error.response.data);
        setErrorMessage(error.response.data.message);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (props.patientId) {
      (async () => {
        await fetchPatients();
        // setPatient(patients.find((pat) => pat.id === props.patientId).id);
      })();
    }
  }, [props.patientId]);

  useEffect(() => {
    if (patients.length >= 1 && props.patientId) {
      const patient = patients.find((pat) => pat.id === props.patientId);
      if (patient) {
        setDeliveryAddress(`${patient.address1}`);
        setCity(patient.city);
        setZipCode(patient.zipcode);
        setDeliveryPhone(patient.cell_phone || patient.home_phone);
        setState(patient.state);
        setShippingService("");
        setPatient(props.patientId);
      }
    }
  }, [props.patientId, patients]);


  useEffect(() => {
    if (
      isMultiPatientDoctorDelivery &&
      facility &&
      !props.patientId &&
      orderMethod !== "Bulk"
    ) {
      setDeliveryAddress(facility.address1 || "");
      setDeliveryPhone(facility.office_phone || "");
      setCity(facility.city || "");
      setZipCode(facility.zipcode || "");
      setState(facility.state || "");
      setShippingService("");
    }
  }, [isMultiPatientDoctorDelivery, facility, props.patientId, orderMethod]);

  const fetchPatients = async () => {
    try {
      const patients = await instance.get("/doctor/patient/all", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPatients(() => patients.data);
    } catch (error) {
      setErrorMessage("Failed to fetch patients, please reload page");
    }
  };

  const handleOrderMethodChange = async (e) => {
    setOrderMethod(e.target.value);
    if (e.target.value === "Bulk") {
      setDeliveryAddress(`${facility.address1}`);
      setDeliveryPhone(facility.office_phone);
      setCity(facility.city);
      setZipCode(facility.zipcode);
      setState(facility.state);
      setShippingService("");
      setOrderBillingPreference("Bill Facility");
      setDeliveryType("Deliver to Doctor");
    } else {
      setDeliveryType("Deliver to Patient");
      setOrderBillingPreference("Bill Patient");
      await fetchPatients();
    }
  };

  const handlePatientSelect = async (e) => {
    setPatient(e.value);
    const patient = patients.find((pat) => pat.id === e.value);
    if (patient) {
      setDeliveryAddress(`${patient.address1}`);
      setDeliveryPhone(patient.cell_phone || patient.home_phone);
      setCity(patient.city);
      setZipCode(patient.zipcode);
      setState(patient.state);
      setShippingService("");
    } else {
      setDeliveryAddress("");
      setDeliveryPhone("");
    }
  };

  const getShippingServices = async (pharmacyId, state) => {
    setLoadingServices(true);
    const services = await instance.post(
      "/doctor/pharmacy/shipping-services",
      { pharmacyId, state },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    setShippingServices(
      filterShippingServicesForDeliveryType(services.data, deliveryType).map((service) => ({
        ...service,
        value: service.id,
        label: `${service.name
          } - ${service.carrier?.toUpperCase()} - $${service.price?.toLocaleString()} - ${service.weight
          }LBs (Service level: ${service.service_level}) - ${service.state}`,
      })),
    );
    setLoadingServices(false);
  };

  const handleSelectState = async (value) => {
    setState(value);
    setShippingService("");
  };

  const handlePharmacySelect = async (e) => {
    setPharmacy(e.target.value);
    try {
      if (props.medType === "compound") {
        const formulations = await instance.post(
          "/doctor/pharmacy/formulations",
          { pharmacyId: e.target.value },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setFormulations(formulations.data);
        if (formulations.data.length === 0) {
          toast.info("No formulations found for this pharmacy");
        }
      } else {
        const drugs = await instance.post(
          "/doctor/pharmacy/drugs",
          { pharmacyId: e.target.value },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setPharmacyDrugs(
          drugs.data.map((drug) => ({
            ...drug,
            id: drug.id,
            label: `${drug.name} - ${drug.mg} - ${drug.size}`,
          })),
        );
      }
    } catch (error) {
      setErrorMessage("Failed to fetch pharmacy drugs, please reload page");
    }
  };

  const fetchPatientShippingServices = async (index, pharmacyId, stateVal) => {
    setMultiPatientOrders((prev) => {
      const newOrders = [...prev];
      if (newOrders[index]) newOrders[index].loadingServices = true;
      return newOrders;
    });

    try {
      const services = await instance.post(
        "/doctor/pharmacy/shipping-services",
        { pharmacyId, state: stateVal },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const options = filterShippingServicesForDeliveryType(
        services.data,
        deliveryType,
      ).map((service) => ({
        ...service,
        value: service.id,
        label: `${service.name
          } - ${service.carrier?.toUpperCase()} - $${service.price?.toLocaleString()} - ${service.weight
          }LBs (Service level: ${service.service_level}) - ${service.state}`,
      }));

      setMultiPatientOrders((prev) => {
        const newOrders = [...prev];
        if (newOrders[index]) {
          newOrders[index].loadingServices = false;
          newOrders[index].shippingServicesOptions = options;
        }
        return newOrders;
      });
    } catch (error) {
      setMultiPatientOrders((prev) => {
        const newOrders = [...prev];
        if (newOrders[index]) newOrders[index].loadingServices = false;
        return newOrders;
      });
    }
  };

  const updatePatientOrder = (index, field, value) => {
    setMultiPatientOrders((prev) => {
      const newOrders = [...prev];
      if (newOrders[index]) {
        newOrders[index][field] = value;
      }
      return newOrders;
    });

    if (field === "state" && pharmacy) {
      fetchPatientShippingServices(index, pharmacy, value);
    }
  };

  const handleMultiPatientChange = (newValue) => {
    const newOrders = newValue.map((p) => {
      const existing = multiPatientOrders.find((o) => o.patientId === p.id);
      if (existing) return existing;

      return {
        patientId: p.id,
        patientName: `${p.lastname} ${p.firstname}`,
        deliveryAddress: p.address1 || "",
        city: p.city || "",
        zipCode: p.zipcode || "",
        deliveryPhone: p.cell_phone || p.home_phone || "",
        state: p.state || "",
        shippingService: "",
        shippingServicesOptions: [],
        loadingServices: false,
        medications: [createEmptyMedication()],
      };
    });

    setMultiPatientOrders(newOrders);

    if (!isMultiPatientDoctorDelivery) {
      newOrders.forEach((order, idx) => {
        if (
          pharmacy &&
          order.state &&
          (!order.shippingServicesOptions ||
            order.shippingServicesOptions.length === 0)
        ) {
          fetchPatientShippingServices(idx, pharmacy, order.state);
        }
      });
    }
  };

  const handleCsvImport = (importedData) => {
    const newItems = importedData.map((row) => ({
      patientId: row.id,
      patientName: row.patientName,
      deliveryAddress: row.delivery_address || "",
      city: row.delivery_city || "",
      zipCode: row.zipcode || "",
      deliveryPhone: row.delivery_phone || "",
      state: row.delivery_state || "",
      shippingService: "",
      shippingServicesOptions: [],
      loadingServices: false,
      medications: [createEmptyMedication()],
    }));

    const filteredNewItems = newItems.filter(
      (newItem) =>
        !multiPatientOrders.some(
          (existing) => existing.patientId === newItem.patientId,
        ),
    );

    const updatedOrders = [...multiPatientOrders, ...filteredNewItems];
    setMultiPatientOrders(updatedOrders);

    const startIndex = multiPatientOrders.length;

    if (!isMultiPatientDoctorDelivery) {
      filteredNewItems.forEach((order, index) => {
        if (pharmacy && order.state) {
          fetchPatientShippingServices(
            startIndex + index,
            pharmacy,
            order.state,
          );
        }
      });
    }

    if (filteredNewItems.length > 0) {
      toast.success(`Added ${filteredNewItems.length} patients from CSV`);
    } else if (newItems.length > 0) {
      toast.info("All patients in CSV were already selected");
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (mounted && pharmacy && state) {
        await getShippingServices(pharmacy, state);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [pharmacy, state, deliveryType]);

  useEffect(() => {
    if (deliveryType !== "Deliver to Doctor") {
      return;
    }

    const selectedShippingServiceName = `${shippingService?.name || ""} ${shippingService?.service_level || ""}`
      .toLowerCase()
      .trim();

    if (selectedShippingServiceName.includes("pickup") || selectedShippingServiceName.includes("pick up")) {
      setShippingService("");
    }

    setMultiPatientOrders((prev) =>
      prev.map((order) => {
        const orderShippingServiceName = `${order?.shippingService?.name || ""} ${order?.shippingService?.service_level || ""}`
          .toLowerCase()
          .trim();

        if (orderShippingServiceName.includes("pickup") || orderShippingServiceName.includes("pick up")) {
          return {
            ...order,
            shippingService: "",
          };
        }

        return order;
      }),
    );
  }, [deliveryType, shippingService]);

  const generateRows = () => {
    setNumberOfRows((num) => num + 1);
    const id = generateRandomId();
    setMedications((prev) => updateMedication(prev, numberOfRows, "id", id));
  };

  useEffect(() => {
    if (selectedRx.length >= 1) {
      setNumberOfRows((prev) => {
        const len = selectedRx.length;
        setMedications(() => [
          ...selectedRx.map((id) => ({
            amount: "",
            c2: "",
            category: "",
            device: "",
            form: "",
            id: generateRandomId(),
            mg: "",
            quantity: "1",
            route_of_admin: "",
            size: "",
            strength: "",
            rxId: id,
          })),
        ]);

        return len;
      });
    } else {
      const id = generateRandomId();
      setMedications((prev) =>
        updateMedication(prev, numberOfRows - 1, "id", id),
      );
    }
  }, [props.selectedRx]);

  const handleSelectShippingService = (value) => {
    setShippingService(value);
  };

  const addMultiPatientMedication = (patientIndex) => {
    setMultiPatientOrders((prev) => {
      const newOrders = [...prev];
      const patientOrder = newOrders[patientIndex];
      if (!patientOrder) return prev;

      patientOrder.medications = [
        ...(patientOrder.medications || []),
        createEmptyMedication(),
      ];

      return newOrders;
    });
  };

  const updateMultiPatientMedication = (
    patientIndex,
    medicationIndex,
    field,
    value,
  ) => {
    setMultiPatientOrders((prev) => {
      const newOrders = [...prev];
      const patientOrder = newOrders[patientIndex];
      if (!patientOrder) return prev;

      const nextMedications = [...(patientOrder.medications || [])];
      const medication =
        nextMedications[medicationIndex] || createEmptyMedication();
      medication[field] = value;
      nextMedications[medicationIndex] = medication;
      patientOrder.medications = nextMedications;

      return newOrders;
    });
  };

  const removeMultiPatientMedication = (patientIndex, medicationId) => {
    setMultiPatientOrders((prev) => {
      const newOrders = [...prev];
      const patientOrder = newOrders[patientIndex];
      if (!patientOrder) return prev;

      const currentMedications = patientOrder.medications || [];
      if (currentMedications.length <= 1) {
        return prev;
      }

      patientOrder.medications = currentMedications.filter(
        (medication) => medication.id !== medicationId,
      );

      return newOrders;
    });
  };

  const removeMedication = (id) => {
    if (medications.length > 1) {
      setMedications(medications.filter((m) => m.id !== id));
      if (editingMedication?.id === id) {
        setEditingMedication(null);
      }
      setNumberOfRows((prev) => prev - 1);
    }
  };

  return (
    <>
      {order && (
        <OrderSummaryModal
          order={order}
          isRefill={false}
          onClose={() => {
            setOrder(false);
            props.triggerRevalidate();
            props.onClose();
          }}
          showNextSteps={
            order.amount && order.payment_status.toLowerCase() !== "paid"
          }
        />
      )}
      <AddPatientModal
        isOpen={isAddPatientModalOpen}
        onClose={() => setIsAddPatientModalOpen(false)}
        triggerRevalidate={(data) => {
          // console.log(data);
          setPatients((prev) => [...prev, data]);
          setPatient(data.id);
          setDeliveryAddress(data.address1);
          setCity(data.city);
          setZipCode(data.zipcode);
          setDeliveryPhone(data.cell_phone || data.home_phone);
          setState(data.state);
          setShippingService("");
        }}
      />
      <UploadPatientsCsvModal
        isOpen={isUploadCsvModalOpen}
        onClose={() => setIsUploadCsvModalOpen(false)}
        patients={patients}
        onConfirm={handleCsvImport}
        fetchPatients={fetchPatients}
      />
      <NewPractitionerModal
        isOpen={isAddDoctorModalOpen}
        onClose={() => setIsAddDoctorModalOpen(false)}
        onCreated={async () => {
          await fetchDoctors();
        }}
      />
      <form
        className={`${props.isOpen ? "block" : "hidden"
          } fixed top-0 right-0 left-0 bottom-0 z-40 bg-white overflow-y-scroll scrollbar-hide`}
        onSubmit={handleCreateOrder}
      >
        <div className="sticky left-0 right-0 top-0 z-50">
          <div className="bg-[#f1f1f1] px-5 py-4 flex justify-between">
            <div className="text-[18px] font-bold select-none">
              Create Order (Rx)
            </div>
            <button onClick={props.onClose}>
              <CloseSquare variant="Bold" />
            </button>
          </div>
        </div>

        <div className="px-5 py-3 text-[14px] space-y-4 mt-4">
          <div className="!mt-5 grid grid-cols-4 gap-x-5 mb-5">
            <NormalSelectInputField
              title="Order Method"
              values={[
                { label: "Bulk Order/Office use", value: "Bulk" },
                { label: "Patient Order", value: "Patient" },
                { label: "Multi-Patient Order", value: "multi-patient" },
              ]}
              required={true}
              value={orderMethod}
              onChange={handleOrderMethodChange}
            />
            <NormalSelectInputField
              title="Order Type"
              values={["New", "Refill"]}
              required={true}
              value={orderType}
              onChange={(e) => setOrderType(e.target.value)}
            />
            <NormalSelectInputField
              title="Billing Preference"
              values={
                orderMethod !== "Bulk"
                  ? ["Bill Facility", "Bill Patient"]
                  : ["Bill Facility"]
              }
              required={true}
              value={orderBillingPreference}
              onChange={(e) => setOrderBillingPreference(e.target.value)}
            />

            <NormalSelectInputField
              title="Delivery Type"
              values={
                orderMethod !== "Bulk"
                  ? ["Deliver to Patient", "Deliver to Doctor"]
                  : ["Deliver to Doctor"]
              }
              required={true}
              value={deliveryType}
              onChange={(e) => setDeliveryType(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-[690px_minmax(0,1fr)] gap-x-5 items-end">
            <div>
              <div className="font-semibold uppercase text-[12px] text-gray-600">
                Select supervising practitioner <span className="text-red-500">*</span>
              </div>
              <div className="flex items-center gap-3">
                <select
                  onChange={(e) => setSupervisingPractitionerId(e.target.value)}
                  value={supervisingPractitionerId}
                  required={true}
                  className="h-[44px] border text-sm border-gray-300 focus:border-primary duration-300 p-2 w-full rounded-[4px] font-medium bg-inherit pointer-events-auto cursor-auto opacity-100"
                >
                  <option value="">--Select--</option>
                  {doctors.map((item, index) =>
                    item?.value ? (
                      <option key={index} value={item.value} className="block">
                        {item.label}
                      </option>
                    ) : (
                      <option key={index} value={item} className="block">
                        {item}
                      </option>
                    ),
                  )}
                </select>
                <button
                  type="button"
                  onClick={() => setIsAddDoctorModalOpen(true)}
                  className="h-[44px] shrink-0 rounded-[4px] bg-primary px-4 text-[13px] font-medium text-white shadow-sm transition-opacity hover:opacity-80"
                >
                  New practitioner
                </button>
              </div>
            </div>
            <NormalSelectInputField
              title="Select a Pharmacy"
              values={pharmacies}
              required={true}
              onChange={handlePharmacySelect}
            />
          </div>
          <div
            className={`grid ${orderMethod === "Patient" ? "grid-cols-[30%_auto]" : "grid-cols-1"
              } gap-x-5 mb-5`}
          >
            {orderMethod === "Patient" ? (
              <div>
                <Autocomplete
                  id="combo-box-demo"
                  disabled={!pharmacy}
                  options={patients.map((patient) => ({
                    label: `${patient.lastname} ${patient.firstname} - ID${patient.id}`,
                    value: patient.id,
                  }))}
                  value={
                    patients
                      .map((patient) => ({
                        label: `${patient.lastname} ${patient.firstname} - ID${patient.id}`,
                        value: patient.id,
                      }))
                      .find((p) => p.value === patient) || null
                  }
                  onChange={(e, val) => handlePatientSelect(val)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select a Patient"
                      required={true}
                    />
                  )}
                />
                <span
                  className="underline text-sm text-primary w-fit block mt-1 cursor-pointer"
                  onClick={() => setIsAddPatientModalOpen(true)}
                >
                  Add patient
                </span>
              </div>
            ) : (
              orderMethod === "multi-patient" && (
                <div>
                  <Autocomplete
                    multiple
                    disabled={!pharmacy}
                    id="multi-patient-select"
                    options={patients}
                    getOptionLabel={(option) =>
                      `${option.lastname} ${option.firstname} - ID${option.id}`
                    }
                    value={patients.filter((p) =>
                      multiPatientOrders.some((m) => m.patientId === p.id),
                    )}
                    onChange={(e, val) => handleMultiPatientChange(val)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Patients"
                        placeholder="Select multiple patients"
                      />
                    )}
                  />
                  <span
                    className="underline text-sm text-primary w-fit block mt-1 cursor-pointer"
                    onClick={() => {
                      if (pharmacy) {
                        setIsUploadCsvModalOpen(true);
                      } else {
                        toast.info("Please select a pharmacy first");
                      }
                    }}
                  >
                    Upload CSV
                  </span>
                </div>
              )
            )}
            {orderMethod !== "multi-patient" && (
              <div className="grid grid-cols-3 gap-x-5">
                <NormalInputField
                  title="Delivery Address"
                  required={true}
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                />
                <NormalInputField
                  title="Delivery City"
                  required={true}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
                <NormalInputField
                  title="Delivery Phone"
                  required={true}
                  value={deliveryPhone}
                  onChange={(e) => setDeliveryPhone(e.target.value)}
                />
              </div>
            )}
          </div>
          {orderMethod !== "multi-patient" && (
            <div className="grid grid-cols-[30%_15%_auto] items-center gap-x-5">
              <NormalSelectInputField
                title="Delivery State"
                values={countries
                  .find((country) => country.code2 === "US")
                  .states.map((state) => state.name)}
                value={state}
                required={true}
                onChange={(e) => handleSelectState(e.target.value)}
              />
              <NormalInputField
                title="Zip Code"
                required={true}
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
              />
              <Autocomplete
                id="combo-box-demo"
                options={filterShippingServicesForDeliveryType(shippingServices, deliveryType)}
                onChange={(e, value) => handleSelectShippingService(value)}
                value={shippingService}
                disabled={!pharmacy || !state}
                loading={loadingServices}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Shipping service"
                    required={true}
                  />
                )}
              />
            </div>
          )}

          {orderMethod === "multi-patient" && (
            <div className="mt-5 space-y-5">
              {isMultiPatientDoctorDelivery && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="mb-3 text-[15px] font-semibold text-gray-900">
                    Delivery details
                  </div>
                  <div className="grid grid-cols-3 gap-5 mb-4">
                    <NormalInputField
                      title="Delivery Address"
                      required={true}
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                    />
                    <NormalInputField
                      title="Delivery City"
                      required={true}
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                    />
                    <NormalInputField
                      title="Delivery Phone"
                      required={true}
                      value={deliveryPhone}
                      onChange={(e) => setDeliveryPhone(e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-[30%_15%_auto] items-center gap-x-5">
                    <NormalSelectInputField
                      title="Delivery State"
                      values={countries
                        .find((country) => country.code2 === "US")
                        .states.map((state) => state.name)}
                      value={state}
                      required={true}
                      onChange={(e) => handleSelectState(e.target.value)}
                    />
                    <NormalInputField
                      title="Zip Code"
                      required={true}
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value)}
                    />
                    <Autocomplete
                      id="multi-patient-doctor-shipping-service"
                      options={filterShippingServicesForDeliveryType(shippingServices, deliveryType)}
                      onChange={(e, value) => handleSelectShippingService(value)}
                      value={shippingService}
                      disabled={!pharmacy || !state}
                      loading={loadingServices}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Shipping service"
                          required={true}
                        />
                      )}
                    />
                  </div>
                </div>
              )}
              {multiPatientOrders.map((order, index) => (
                <div
                  key={order.patientId}
                  className="border rounded-lg p-4 bg-gray-50 relative"
                >
                  <div
                    className="font-bold flex justify-between items-center cursor-pointer"
                    onClick={() =>
                      setOpenAccordionIndex((prev) =>
                        prev === index ? -1 : index,
                      )
                    }
                  >
                    <div className="flex items-center gap-2">
                      {openAccordionIndex === index ? (
                        <ArrowUp2 size="16" />
                      ) : (
                        <ArrowDown2 size="16" />
                      )}
                      <span>{order.patientName}</span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMultiPatientChange(
                          patients.filter((p) =>
                            multiPatientOrders
                              .filter((_, i) => i !== index)
                              .some((m) => m.patientId === p.id),
                          ),
                        );
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash size="16" />
                    </button>
                  </div>
                  {openAccordionIndex === index && (
                    <>
                      {!isMultiPatientDoctorDelivery && (
                        <>
                          <div className="grid grid-cols-3 gap-5 mb-4 mt-3">
                            <NormalInputField
                              title="Delivery Address"
                              required={true}
                              value={order.deliveryAddress}
                              onChange={(e) =>
                                updatePatientOrder(
                                  index,
                                  "deliveryAddress",
                                  e.target.value,
                                )
                              }
                            />
                            <NormalInputField
                              title="Delivery City"
                              required={true}
                              value={order.city}
                              onChange={(e) =>
                                updatePatientOrder(index, "city", e.target.value)
                              }
                            />
                            <NormalInputField
                              title="Delivery Phone"
                              required={true}
                              value={order.deliveryPhone}
                              onChange={(e) =>
                                updatePatientOrder(
                                  index,
                                  "deliveryPhone",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                          <div className="grid grid-cols-[30%_15%_auto] items-center gap-x-5">
                            <NormalSelectInputField
                              title="Delivery State"
                              values={countries
                                .find((country) => country.code2 === "US")
                                .states.map((state) => state.name)}
                              value={order.state}
                              required={true}
                              onChange={(e) =>
                                updatePatientOrder(index, "state", e.target.value)
                              }
                            />
                            <NormalInputField
                              title="Zip Code"
                              required={true}
                              value={order.zipCode}
                              onChange={(e) =>
                                updatePatientOrder(index, "zipCode", e.target.value)
                              }
                            />
                            <Autocomplete
                              id={`shipping-service-${index}`}
                              options={filterShippingServicesForDeliveryType(order.shippingServicesOptions || [], deliveryType)}
                              onChange={(e, value) =>
                                updatePatientOrder(index, "shippingService", value)
                              }
                              value={order.shippingService}
                              disabled={!pharmacy || !order.state}
                              loading={order.loadingServices}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Shipping service"
                                  required={true}
                                />
                              )}
                            />
                          </div>
                        </>
                      )}
                      <div className="mt-5">
                        <div className="font-semibold text-[16px] mb-3">
                          Prescriptions
                        </div>
                        <div className="space-y-3">
                          {(order.medications || []).map(
                            (medication, medicationIndex) => (
                              <div
                                key={medication.id || medicationIndex}
                                className="bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-all cursor-pointer"
                                onClick={() =>
                                  setEditingMedication({
                                    ...medication,
                                    index: medicationIndex,
                                    patientIndex: index,
                                    source: "multi-patient",
                                  })
                                }
                              >
                                <div className="p-4">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3 flex-1">
                                      <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium">
                                        {medicationIndex + 1}
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <h4 className="font-medium text-gray-900">
                                            {medication.name ||
                                              `Medication ${medicationIndex + 1}`}
                                          </h4>
                                        </div>
                                        <div className="text-sm text-gray-500 mt-1">
                                          {medication.quantity && medication.mg
                                            ? `${medication.quantity} units • ${medication.mg}`
                                            : "Click to add details"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingMedication({
                                            ...medication,
                                            index: medicationIndex,
                                            patientIndex: index,
                                            source: "multi-patient",
                                          });
                                        }}
                                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-md transition-colors"
                                        type="button"
                                      >
                                        <Edit2 className="w-4 h-4" />
                                      </button>
                                      {(order.medications || []).length > 1 && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            removeMultiPatientMedication(
                                              index,
                                              medication.id,
                                            );
                                          }}
                                          className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                          type="button"
                                        >
                                          <Trash className="w-4 h-4" />
                                        </button>
                                      )}
                                      <ArrowRight2 className="w-4 h-4 text-gray-400" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                        <button
                          onClick={() => addMultiPatientMedication(index)}
                          className="bg-primary px-3 mt-3 flex justify-center items-center shadow-sm text-sm font-medium py-2 rounded-[4px] text-white"
                          type="button"
                        >
                          + Add Prescription
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 py-3 mt-7">
          {orderMethod !== "multi-patient" && (
            <>
              <div className="font-bold text-[18px] mb-4">
                Create Prescription
              </div>
              {props.medType === "compound" ? (
                <PrescriptionForCompoundOrder
                  formulations={formulations}
                  isRefill={orderType === "Refill"}
                  rxId={medications[0]?.rxId}
                  onChange={(type, data) =>
                    setMedications((prev) =>
                      updateMedication(prev, 0, type, data),
                    )
                  }
                  patientOrder={orderMethod.toLowerCase() !== "bulk"}
                />
              ) : (
                <div>
                  <div className="mt-5">
                    {medications.map((medication, index) => (
                      <div
                        key={medication.id}
                        className="bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-all cursor-pointer mb-5"
                        onClick={() =>
                          setEditingMedication({ ...medication, index })
                        }
                      >
                        <div className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                              <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium text-gray-900">
                                    {medication.name ||
                                      `Medication ${index + 1}`}
                                  </h4>
                                </div>
                                <div className="text-sm text-gray-500 mt-1">
                                  {medication.quantity && medication.mg
                                    ? `${medication.quantity} units • ${medication.mg}`
                                    : "Click to add details"}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingMedication({
                                    ...medication,
                                    index,
                                  });
                                }}
                                className="p-2 text-gray-500 hover:bg-gray-100 rounded-md transition-colors"
                                type="button"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              {medications.length > 1 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeMedication(medication.id);
                                  }}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                  type="button"
                                >
                                  <Trash className="w-4 h-4" />
                                </button>
                              )}
                              <ArrowRight2 className="w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    disabled={!numberOfRows ? true : false}
                    onClick={() => {
                      generateRows();
                    }}
                    className="bg-primary px-3 flex justify-center items-center shadow-sm text-sm font-medium py-2 rounded-[4px] text-white"
                    type="button"
                  >
                    + Add Prescription
                  </button>
                </div>
              )}
            </>
          )}

          <div className="mt-4">
            <div>
              <div className="font-bold text-[18px] mb-2">
                Add Order Note {props.medType === "compound" ? "*" : ""}
              </div>
              <textarea
                onChange={(e) => setOrderNote(e.target.value)}
                className="w-full border rounded-[4px] p-2 outline-none resize-none"
                placeholder="Enter order note"
                rows={7}
                required={props.medType === "compound"}
              />
            </div>
          </div>

          <div className="mt-4">
            <div className="font-bold text-[16px] mb-2">
              Attach Clinical/Prescription Document
            </div>
            <div className="flex items-center gap-x-2">
              <DocumentUpload size="22" color="#00B0AD" />{" "}
              <input
                type="file"
                placeholder="Add an attachment"
                accept=".pdf,.docx,.xlsx"
                className="text-sm opacity-80"
                onChange={onFileChange}
              />
            </div>
          </div>

          <div className="mt-4">
            <div className="text-[14px] font-semibold py-1 text-red-500">
              {errorMessage}
            </div>
            {/* <button onClick={handleCreateOrder}  className="bg-[#00B0AD] px-3 py-2 rounded-[4px] shadow-sm text-white font-medium">Create Order</button> */}
            <PrimaryAddButton
              title="Create Order +"
              onClick={handleCreateOrder}
              isLoading={isLoading}
              disabled={isButtonNotActive || isLoading}
              color="white"
              type="submit"
            />
          </div>
        </div>
      </form>
      {editingMedication && (
        <RxItem
          medication={editingMedication}
          drugs={pharmacyDrugs}
          refill={orderType === "Refill"}
          billPatient={orderBillingPreference.toLowerCase() === "bill patient"}
          rxId={
            editingMedication.source === "multi-patient"
              ? multiPatientOrders[editingMedication.patientIndex]
                ?.medications?.[editingMedication.index]?.rxId
              : medications[editingMedication.index]?.rxId
          }
          onChange={(type, data) =>
            editingMedication.source === "multi-patient"
              ? updateMultiPatientMedication(
                editingMedication.patientIndex,
                editingMedication.index,
                type,
                data,
              )
              : setMedications((prev) =>
                updateMedication(prev, editingMedication.index, type, data),
              )
          }
          onSave={(data) => {
            if (editingMedication.source === "multi-patient") {
              setMultiPatientOrders((prevOrders) => {
                const updatedOrders = [...prevOrders];
                const patientOrder = {
                  ...updatedOrders[editingMedication.patientIndex],
                };
                const nextMedications = [...(patientOrder.medications || [])];
                nextMedications[editingMedication.index] = data;
                patientOrder.medications = nextMedications;
                updatedOrders[editingMedication.patientIndex] = patientOrder;
                return updatedOrders;
              });
              return;
            }

            setMedications((prevMedications) => {
              const updatedMedications = [...prevMedications];
              updatedMedications[editingMedication.index] = data;
              return updatedMedications;
            });
          }}
          onClose={() => setEditingMedication(null)}
          patientOrder={orderMethod.toLowerCase() !== "bulk"}
        />
      )}
      {editingMedication && (
        <div className="fixed inset-0 bg-[rgba(0,0,0,.2)] z-40" />
      )}
    </>
  );
}

function RxItem(props) {
  const medication = props.medication;
  const directionOptions = Array.isArray(directionsSigs) ? directionsSigs : [];
  const getSavedDirectionEntries = (selectedMedication = medication) => {
    if (
      Array.isArray(selectedMedication.directions) &&
      selectedMedication.directions.some((direction) =>
        getDirectionText(direction)?.trim(),
      )
    ) {
      return selectedMedication.directions;
    }

    if (selectedMedication.direction) {
      return String(selectedMedication.direction)
        .split("|")
        .map((direction, index) => ({
          month: index + 1,
          label: getMonthDirectionLabel(index + 1),
          text: direction.trim(),
        }));
    }

    return [];
  };
  const getMedicationFormData = (selectedMedication = medication) => ({
    name: selectedMedication.name || "",
    mg: selectedMedication.mg || "",
    size: selectedMedication.size || "",
    device: selectedMedication.device || "",
    route_of_admin: selectedMedication.route_of_admin || "",
    form: selectedMedication.form || "",
    category: selectedMedication.category || "",
    strength: selectedMedication.strength || "",
    c2: selectedMedication.c2 || "",
    amount: selectedMedication.amount || "",
    quantity: selectedMedication.quantity || "1",
    drugId: selectedMedication.drugId || "",
    id: selectedMedication.id || "",
    direction: selectedMedication.direction || "",
    directions: props.patientOrder
      ? buildDirectionEntries(
        getDirectionCountFromDaysSupply(
          selectedMedication.daysOfSupply || selectedMedication.dayOfSupply,
        ),
        getSavedDirectionEntries(selectedMedication),
      )
      : [],
    daysOfSupply:
      selectedMedication.daysOfSupply || selectedMedication.dayOfSupply || "",
    rxId: selectedMedication.rxId || props.rxId,
    reasonForCompounding: selectedMedication.reasonForCompounding || "",
    diagnosisCode: selectedMedication.diagnosisCode || "",
    refills: selectedMedication.refills || "",
    drugPrice: selectedMedication.drugPrice || "",
    additionalNote: selectedMedication.additionalNote || "",
  });

  const [daysOfSupply, setDaysOfSupply] = useState([]);

  const [formData, setFormData] = useState(() => getMedicationFormData());

  useEffect(() => {
    setFormData(getMedicationFormData(medication));
  }, [medication.id, medication.index, medication.source, props.rxId]);

  useEffect(() => {
    console.log(formData.directions);
  }, [formData]);

  const handleSelectDrug = (value) => {
    if (value) {
      const drug = props.drugs.find((drug) => drug.id === value.id);
      const amount = props.billPatient
        ? "" // used to be patient amount
        : drug.doctor_amount;
      const parsedDaysOfSupply = !drug?.dos ? [] : JSON.parse(drug?.dos);

      setDaysOfSupply(parsedDaysOfSupply);
      setFormData((prev) => ({
        ...prev,
        name: drug.name,
        amount,
        drugId: value.id,
        c2: drug.c2,
        mg: drug.mg,
        size: drug.size,
        device: drug.device,
        route_of_admin: drug.route_of_admin,
        form: drug.form,
        category: drug.category,
        strength: drug.strength,
        drugPrice: amount,
        quantity: "1",
        dayOfSupply: "",
        daysOfSupply: "",
        direction: "",
        directions: props.patientOrder ? buildDirectionEntries(1) : [],
        rxId: prev.rxId || props.rxId,
      }));
    }
  };

  const updateFormData = (key, value) => {
    setFormData((prev) => {
      if (key === "daysOfSupply") {
        const directions = buildDirectionEntries(
          getDirectionCountFromDaysSupply(value),
          prev.directions,
        );

        return {
          ...prev,
          dayOfSupply: value,
          daysOfSupply: value,
          directions,
          direction: directions
            .map((entry) => entry.text?.trim())
            .filter(Boolean)
            .join(" | "),
        };
      }

      return { ...prev, [key]: value };
    });
  };

  const updateDirectionEntry = (index, value) => {
    setFormData((prev) => {
      const directions = props.patientOrder
        ? buildDirectionEntries(
          getDirectionCountFromDaysSupply(
            prev.daysOfSupply || prev.dayOfSupply,
          ),
          prev.directions,
        ).map((entry, entryIndex) =>
          entryIndex === index ? { ...entry, text: value } : entry,
        )
        : [];

      return {
        ...prev,
        directions,
        direction: directions
          .map((entry) => entry.text?.trim())
          .filter(Boolean)
          .join(" | "),
      };
    });
  };

  const renderDirectionAutocomplete = ({
    key,
    title,
    value,
    onChange,
    placeholder,
  }) => (
    <Autocomplete
      key={key}
      freeSolo
      options={directionOptions}
      value={value || ""}
      inputValue={value || ""}
      onInputChange={(event, inputValue, reason) => {
        if (reason !== "reset") {
          onChange(inputValue);
        }
      }}
      onChange={(event, newValue) => {
        onChange(typeof newValue === "string" ? newValue : (newValue ?? ""));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          required
          label={title}
          placeholder={placeholder}
          size="small"
        />
      )}
    />
  );

  const getPatientOrderBaseAmount = (selectedDays) => {
    return +(
      daysOfSupply?.find((item) => item.number_of_days == selectedDays)?.amount || 0
    );
  };

  const handleQuantityUpdate = (value) => {
    updateFormData("quantity", value);
    if (props.patientOrder) {
      const baseAmount = getPatientOrderBaseAmount(
        formData.daysOfSupply || formData.dayOfSupply
      );

      if (+value >= 1 && baseAmount) {
        updateFormData("amount", baseAmount * +value);
      }
      return;
    }

    if (formData.drugPrice) {
      +value >= 1 && updateFormData("amount", +formData.drugPrice * +value);
    }
  };

  const isBtnDisabled = () => {
    if (props.rxId && !formData.rxId) return true;

    if (props.patientOrder && !formData.reasonForCompounding) return true;

    const hasInvalidDirections = props.patientOrder
      ? buildDirectionEntries(
        getDirectionCountFromDaysSupply(
          formData.daysOfSupply || formData.dayOfSupply,
        ),
        formData.directions,
      ).some((entry) => !entry.text?.trim())
      : !formData.direction;

    return (
      !formData.mg ||
      +formData.quantity < 1 ||
      !formData.name ||
      !formData.size ||
      hasInvalidDirections
    );
  };

  const handleSaveChanges = () => {
    const normalizedFormData = normalizeMedicationDirections(
      formData,
      props.patientOrder,
    );

    for (const item in normalizedFormData) {
      props.onChange(item, normalizedFormData[item]);
    }
    props.onClose();
  };

  useEffect(() => {
    if (!props.patientOrder) {
      setDaysOfSupply([]);
      setFormData((prev) => ({
        ...prev,
        directions: [],
      }));
      return;
    }

    const selectedDrug = props.drugs.find(
      (drug) => drug.id === formData.drugId,
    );
    const parsedDaysOfSupply = !selectedDrug?.dos
      ? []
      : JSON.parse(selectedDrug.dos);

    setDaysOfSupply(parsedDaysOfSupply);
    setFormData((prev) =>
      normalizeMedicationDirections(prev, props.patientOrder),
    );
  }, [props.patientOrder, formData.drugId]);

  return (
    <div className="fixed top-0 bottom-0 right-0 w-[600px] bg-white py-3 px-3 z-50 flex flex-col justify-between">
      <div>
        <div className="flex justify-between gap-x-3 items-center bg-[#f1f1f1] -mt-3 -mx-3 px-3 py-3">
          <h2 className="text-lg text-bold">Edit Medication</h2>
          <button onClick={props.onClose} type="button">
            <CloseCircle />
          </button>
        </div>

        <div className="py-5 max-h-[85vh] overflow-y-auto grid grid-cols-2 gap-x-4 gap-y-6">
          <div className="col-span-2">
            <Autocomplete
              disablePortal
              id="combo-box-demo"
              options={props.drugs}
              value={formData.name}
              onChange={(e, value) => handleSelectDrug(value)}
              renderInput={(params) => (
                <TextField {...params} label="Medication Name" required />
              )}
              size="small"
            />
          </div>
          <NormalInputField
            value={`$${formData.amount?.toLocaleString()}`}
            disabled
            required={true}
            title="Amount"
            onChange={(e) => updateFormData("amount", e.target.value)}
            type="text"
          />

          {/* days of supply here */}
          {props.patientOrder && (
            <>
              <NormalSelectInputField
                values={
                  daysOfSupply.length < 1
                    ? []
                    : daysOfSupply?.map((item) => item.number_of_days)
                }
                required={true}
                value={formData.daysOfSupply || formData.dayOfSupply}
                title="Days of supply"
                onChange={(e) => {
                  const amount = getPatientOrderBaseAmount(e.target.value);
                  updateFormData(
                    "amount",
                    amount * +(formData.quantity || 1)
                  );
                  updateFormData("daysOfSupply", e.target.value);
                }}
              />
              <NormalInputField
                value={formData.quantity}
                placeholder="Enter number of vials"
                required={true}
                title="Number of vials"
                onChange={(e) => handleQuantityUpdate(e.target.value)}
                type="number"
                min={1}
                disabled={!formData.drugId}
              />
              <NormalInputField
                value={formData.mg}
                placeholder="Enter mg"
                required={true}
                title="mG/mL"
                onChange={(e) => updateFormData("mg", e.target.value)}
                type="text"
              />
            </>
          )}

          {!props.patientOrder && (
            <NormalInputField
              value={formData.quantity}
              placeholder="Enter quantity"
              required={true}
              title="Quantity"
              onChange={(e) => handleQuantityUpdate(e.target.value)}
              type="number"
              min={1}
              disabled={!formData.drugId}
            />
          )}
          {props.patientOrder ? (
            <div className="col-span-2 space-y-4">
              {buildDirectionEntries(
                getDirectionCountFromDaysSupply(
                  formData.daysOfSupply || formData.dayOfSupply,
                ),
                formData.directions,
              ).map((direction, index) =>
                renderDirectionAutocomplete({
                  key: direction.label,
                  title: direction.label,
                  value: direction.text,
                  placeholder: `Search or enter ${direction.label}`,
                  onChange: (value) => updateDirectionEntry(index, value),
                }),
              )}
            </div>
          ) : (
            renderDirectionAutocomplete({
              key: "direction",
              title: "Direction",
              value: formData.direction,
              placeholder: "Search or enter direction",
              onChange: (value) => updateFormData("direction", value),
            })
          )}

          {!props.patientOrder && (
            <NormalInputField
              value={formData.mg}
              placeholder="Enter mg"
              required={true}
              title="mG/mL"
              onChange={(e) => updateFormData("mg", e.target.value)}
              type="text"
            />
          )}
          {props.patientOrder && (
            <div className="col-span-2 space-y-5">
              <NormalSelectInputField
                values={reasonsForCompounding}
                required={true}
                value={formData.reasonForCompounding}
                title="Medical Necessity Justification"
                onChange={(e) =>
                  updateFormData("reasonForCompounding", e.target.value)
                }
              />
              <NormalTextAreaField
                title="Additional Notes"
                value={formData.additionalNote}
                placeholder="Enter additional notes on medical necessity justification (optional)"
                rows={3}
                onChange={(e) =>
                  updateFormData("additionalNote", e.target.value)
                }
              />
            </div>
          )}

          {props.refill && (
            <NormalInputField
              value={formData.rxId}
              placeholder="Enter RX ID"
              required={true}
              title="Rx ID"
              onChange={(e) => updateFormData("rxId", e.target.value)}
              type="text"
            />
          )}
          <NormalInputField
            value={formData.size}
            placeholder="Enter size"
            required={true}
            title="size"
            onChange={(e) => updateFormData("size", e.target.value)}
            type="text"
          />
          <NormalInputField
            value={formData.route_of_admin}
            placeholder="Enter route of admin"
            title="Route Of Admin"
            onChange={(e) => updateFormData("route_of_admin", e.target.value)}
            type="text"
          />
          <NormalInputField
            value={formData.device}
            placeholder="Enter device"
            title="Device"
            onChange={(e) => updateFormData("device", e.target.value)}
            type="text"
          />
          {formData.strength && (
            <NormalInputField
              value={formData.strength}
              placeholder="Enter strength"
              title="strength"
              onChange={(e) => updateFormData("strength", e.target.value)}
              type="text"
            />
          )}
          {formData.category && (
            <NormalInputField
              value={formData.category}
              placeholder="Enter category"
              title="Category"
              onChange={(e) => updateFormData("category", e.target.value)}
              type="text"
            />
          )}

          {props.patientOrder && (
            <>
              <NormalInputField
                value={formData.diagnosisCode}
                placeholder="Last office visit and diagnosis code"
                title="Diagnosis Code"
                onChange={(e) =>
                  updateFormData("diagnosisCode", e.target.value)
                }
                type="text"
              />
              <NormalInputField
                value={formData.refills}
                placeholder="Enter refills"
                title="Refills"
                onChange={(e) => updateFormData("refills", e.target.value)}
                type="text"
              />
            </>
          )}
        </div>
      </div>
      <div className="bg-white">
        <button
          disabled={isBtnDisabled()}
          onClick={handleSaveChanges}
          type={"button"}
          className={`cursor-pointer bg-primary text-white disabled:cursor-not-allowed disabled:opacity-50 min-w-[200px] w-full flex justify-center items-center py-2 px-3 shadow-sm rounded-[4px] font-semibold text-[14px]`}
        >
          Save Changes
        </button>
      </div>
    </div>
  );
}


function NewPractitionerModal(props) {
  const token = getToken();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [fullName, setFullName] = useState("");
  const [gender, setGender] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [state, setState] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [country, setCountry] = useState("United States");
  const [address, setAddress] = useState("");
  const [license, setLicense] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [dea, setDea] = useState("");
  const [npiNumber, setNpiNumber] = useState("");

  const selectedCountry = countries.find(
    (countryItem) => countryItem.name === country || countryItem.code2 === country,
  );
  const stateOptions = selectedCountry?.states?.map((stateItem) => stateItem.name) || [];

  const isButtonDisabled =
    !fullName ||
    !gender ||
    !email ||
    !phone ||
    !state ||
    !zipcode ||
    !country ||
    !address ||
    !license ||
    !specialty ||
    !dea ||
    !npiNumber ||
    isLoading;

  const resetState = () => {
    setIsLoading(false);
    setErrorMessage("");
    setFullName("");
    setGender("");
    setEmail("");
    setPhone("");
    setState("");
    setZipcode("");
    setCountry("United States");
    setAddress("");
    setLicense("");
    setSpecialty("");
    setDea("");
    setNpiNumber("");
  };

  const closeModal = () => {
    resetState();
    props.onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");

    instance
      .post(
        "/doctor/doctors/new",
        {
          fullname: fullName,
          gender,
          email,
          phone,
          state,
          zipcode,
          country,
          address,
          license,
          specialty,
          dea,
          npiNumber,
          type: "member",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then(async () => {
        await props.onCreated?.();
        closeModal();
      })
      .catch((error) => {
        setErrorMessage(
          error?.response?.data?.message || "Failed to add practitioner",
        );
        setIsLoading(false);
      });
  };

  if (!props.isOpen) {
    return null;
  }

  return (
    <>
      <div className="fixed inset-0 z-[70] bg-[rgba(0,0,0,.25)]" onClick={closeModal} />
      <div className="fixed left-1/2 top-1/2 z-[80] w-full max-w-[720px] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[8px] bg-white shadow-xl">
        <div className="flex items-center justify-between bg-[#f1f1f1] px-5 py-4">
          <div>
            <div className="text-[18px] font-bold select-none">New Practitioner</div>
            <div className="mt-1 text-[13px] text-gray-600">Complete the practitioner details below.</div>
          </div>
          <button type="button" onClick={closeModal}>
            <CloseSquare variant="Bold" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-5 text-[14px] max-h-[75vh] overflow-y-auto scrollbar-hide">
          <div className="grid grid-cols-2 gap-4">
            <NormalInputField
              title="Fullname"
              isRequired={true}
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
            <NormalSelectInputField
              title="Gender"
              values={["Male", "Female", "Others"]}
              isRequired={true}
              value={gender}
              onChange={(e) => setGender(e.target.value)}
            />
            <NormalInputField
              title="Email"
              isRequired={true}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <NormalInputField
              title="Phone"
              isRequired={true}
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <NormalSelectInputField
              title="State"
              values={stateOptions}
              isRequired={true}
              value={state}
              onChange={(e) => setState(e.target.value)}
            />
            <NormalInputField
              title="Zipcode"
              isRequired={true}
              type="text"
              value={zipcode}
              onChange={(e) => setZipcode(e.target.value)}
            />
            <NormalSelectInputField
              title="Country"
              values={countries.map((item) => item.name)}
              isRequired={true}
              value={country}
              onChange={(e) => {
                setCountry(e.target.value);
                setState("");
              }}
            />
            <NormalInputField
              title="License"
              isRequired={true}
              type="text"
              value={license}
              onChange={(e) => setLicense(e.target.value)}
            />
            <NormalSelectInputField
              title="Specialty"
              values={[
                { value: "doctor", label: "Doctor" },
                // { value: "nurse", label: "Nurse" },
                // { value: "worker", label: "Worker" },
              ]}
              isRequired={true}
              value={specialty}
              onChange={(e) => setSpecialty(e.target.value)}
            />
            <NormalInputField
              title="DEA"
              isRequired={true}
              type="text"
              value={dea}
              onChange={(e) => setDea(e.target.value)}
            />
            <NormalInputField
              title="NPI number"
              isRequired={true}
              type="text"
              value={npiNumber}
              onChange={(e) => setNpiNumber(e.target.value)}
            />
          </div>

          <NormalInputField
            title="Address"
            isRequired={true}
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />

          {errorMessage ? (
            <div className="text-[13px] font-medium text-red-500">{errorMessage}</div>
          ) : null}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={closeModal}
              className="rounded-[4px] border border-gray-200 px-4 py-[9px] text-[14px] font-medium text-gray-700"
            >
              Cancel
            </button>
            <PrimaryAddButton
              title="Add Practitioner"
              isLoading={isLoading}
              disabled={isButtonDisabled}
              color="white"
              type="submit"
            />
          </div>
        </form>
      </div>
    </>
  );
}


function PrescriptionForCompoundOrder({
  onChange,
  formulations,
  isRefill,
  rxId,
  patientOrder,
}) {
  const [selectedCompound, setSelectedCompound] = useState(null);
  const [medData, setMedData] = useState({
    id: "",
    name: "",
    price: "",
    size: "",
    direction: "",
    category: "",
    rxId: isRefill ? rxId : "",
    quantity: 1,
    ingredients: [],
  });

  const handleCompoundSelect = (event, value) => {
    setSelectedCompound(value);
    if (value) {
      setMedData((prev) => ({
        ...prev,
        name: value.name,
        price: value.amount,
        size: value.qty,
        direction: "",
        sig: value.sig,
        category: value.category,
        id: value.id,
        ingredients: [...value.ingredients],
      }));
    } else {
      setMedData((prev) => ({
        ...prev,
        name: "",
        price: "",
        size: "",
        direction: "",
        sig: "",
        category: "",
        id: "",
        quantity: 1,
        ingredients: [],
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setMedData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleIngredientChange = (index, field, value) => {
    setMedData((prev) => ({
      ...prev,
      ingredients: prev.ingredients.map((ingredient, i) =>
        i === index ? { ...ingredient, [field]: value } : ingredient,
      ),
    }));
  };

  const addIngredient = () => {
    setMedData((prev) => ({
      ...prev,
      ingredients: [...prev.ingredients, { name: "", quantity: "" }],
    }));
  };

  const removeIngredient = (index) => {
    setMedData((prev) => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index),
    }));
  };

  useEffect(() => {
    onChange("name", medData.name);
    onChange("amount", medData.price * medData.quantity);
    onChange("size", medData.size);
    onChange("direction", medData.direction);
    onChange("rxId", medData.rxId);
    onChange("quantity", medData.quantity);
    onChange("ingredients", medData.ingredients);
    onChange("category", medData.category);
    onChange("formulaId", medData.id);
    onChange("sig", medData.sig);
    onChange("reasonForCompounding", medData.reasonForCompounding);
    onChange("additionalNote", medData.additionalNote);
  }, [medData]);

  return (
    <div>
      {/* Compound Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Compound Medication
        </label>
        <Autocomplete
          options={formulations}
          getOptionLabel={(option) =>
            `${option.name} - ${option.category} ($${option.amount})`
          }
          value={selectedCompound}
          onChange={handleCompoundSelect}
          disabled={formulations.length === 0}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search compound medications..."
              variant="outlined"
              fullWidth
            />
          )}
          renderOption={(props, option) => {
            const { ...rest } = props;
            return (
              <li
                key={option.id}
                {...rest}
                className="px-4 py-2 hover:bg-gray-100"
              >
                <div>
                  <div className="font-medium">{option.name}</div>
                  <div className="text-sm text-gray-600">
                    {option.category} • ${option.amount} •{" "}
                    {option.ingredients.length} ingredients
                  </div>
                </div>
              </li>
            );
          }}
          className="w-full"
        />
      </div>

      {/* Compound Details */}
      {selectedCompound && (
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <NormalInputField
              title="Category"
              required={true}
              value={medData.category}
              disabled
            />
            <NormalInputField
              title="Name"
              required={true}
              value={medData.name}
              disabled
            />

            <NormalInputField
              title="Direction"
              value={medData.direction}
              onChange={(e) => handleInputChange("direction", e.target.value)}
              placeholder="Enter directions for use"
              required={true}
            />
          </div>
          <div
            className={`grid grid-cols-1 ${isRefill ? "md:grid-cols-5" : `md:grid-cols-4`
              } gap-4 mb-4`}
          >
            <NormalInputField
              title="Price"
              required={true}
              value={`$${medData.price * medData.quantity}`}
              disabled
            />
            <NormalInputField
              title="Quantity"
              value={medData.quantity}
              onChange={(e) =>
                handleInputChange("quantity", Number.parseInt(e.target.value))
              }
              type="number"
              min="1"
              placeholder="Quantity"
              required={true}
            />
            <NormalInputField
              title="Size"
              value={medData.size}
              onChange={(e) => handleInputChange("size", e.target.value)}
              placeholder="Size (e.g 100ml)"
              required={true}
            />
            <NormalInputField
              title="SIG"
              value={medData.sig}
              onChange={(e) => handleInputChange("sig", e.target.value)}
              placeholder="Enter SIG"
            />
            {isRefill && (
              <NormalInputField
                title="RX ID"
                value={medData.rxId}
                placeholder="Enter RX ID"
                onChange={(e) => handleInputChange("rxId", e.target.value)}
                required={true}
              />
            )}
          </div>
          {patientOrder && (
            <div className="grid grid-cols-2 gap-4">
              <NormalSelectInputField
                values={reasonsForCompounding}
                required={true}
                value={medData.reasonForCompounding}
                title="Medical Necessity Justification"
                onChange={(e) =>
                  handleInputChange("reasonForCompounding", e.target.value)
                }
              />
              <NormalTextAreaField
                title="Additional Notes"
                value={medData.additionalNote}
                placeholder="Enter additional notes on medical necessity justification (optional)"
                rows={1}
                onChange={(e) =>
                  handleInputChange("additionalNote", e.target.value)
                }
              />
            </div>
          )}
        </div>
      )}

      {/* Ingredients Section */}
      {selectedCompound && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-gray-800">
              Customize Ingredients
            </h4>
            <button
              onClick={addIngredient}
              className="px-4 py-2 bg-primary text-white rounded-md transition-colors text-sm"
              type="button"
            >
              + Add Ingredient
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {medData.ingredients.map((ingredient, index) => (
              <div
                key={index}
                className="flex gap-3 items-center p-3 bg-gray-50 rounded-md"
              >
                <div className="w-8 text-center text-gray-500 font-medium">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <CompoundDrugAutocomplete
                    input={ingredient}
                    handleInputChange={(value) =>
                      handleIngredientChange(index, "name", value.name)
                    }
                  />
                </div>
                <div className="w-32">
                  <NormalInputField
                    title="Quantity"
                    value={ingredient.quantity}
                    onChange={(e) =>
                      handleIngredientChange(index, "quantity", e.target.value)
                    }
                    type="text"
                    placeholder="e.g 100mg"
                    required={true}
                  />
                </div>
                <button onClick={() => removeIngredient(index)} type="button">
                  <CloseSquare
                    variant="Bold"
                    className="text-red-600 hover:text-red-800 duration-300"
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
